/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Schedule syncs with Sequences", lastUpdatedDate = new Date(1686528000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Schedule syncs with Sequences",
        description: null,
        lastUpdatedDate: "Mon Jun 12 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Sequence example",
          "depth": 0,
          "id": "sequence-example"
        }, {
          "value": "Sequence setup",
          "depth": 0,
          "id": "sequence-setup"
        }, {
          "value": "Add syncs to the sequence",
          "depth": 1,
          "id": "add-syncs-to-the-sequence"
        }, {
          "value": "Configuring sync order and error handling",
          "depth": 1,
          "id": "configuring-sync-order-and-error-handling"
        }, {
          "value": "Set your sequence schedule",
          "depth": 1,
          "id": "set-your-sequence-schedule"
        }, {
          "value": "Sequence management",
          "depth": 0,
          "id": "sequence-management"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      strong: "strong",
      ol: "ol",
      li: "li",
      a: "a",
      img: "img",
      ul: "ul",
      h3: "h3"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Sequences"
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync ", _jsx(_components.strong, {
          children: "Sequences"
        }), " allow you to execute a series of related syncs in a predefined order. Sequences are helpful when you have dependencies between syncs. For example, you may have two syncs you need to run in order:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Sync A creates a set of new contacts in your CRM"
        }), "\n", _jsx(_components.li, {
          children: "Sync B adds the new contacts to their associated accounts"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Without Sequences, you would have to schedule the two syncs individually to give the first enough time to finish before the second starts. It would be challenging to guarantee the correct behavior since sometimes sync duration can vary."
      }), "\n", _jsxs(_components.p, {
        children: ["Sequences make orchestration a first-class feature in Hightouch so you can control how and when to sync data to your destinations. You can schedule sequences to run on various ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui#schedule-types",
          children: "schedules"
        }), ", such as ", _jsx(_components.a, {
          href: "/extensions/dbt-cloud",
          children: "triggered by dbt Cloud runs"
        }), " or ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui#cron-expression-schedules",
          children: "cron schedules"
        }), ", just as you would with syncs. You can also ", _jsx(_components.a, {
          href: "#sequence-management",
          children: "initiate sequences manually"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sequence-example",
        children: "Sequence example"
      }), "\n", _jsx(_components.p, {
        children: "Suppose you manage your staff members, their permissions, customer information, and staff customer assignments using Hightouch. To do so, you use four syncs that need to run in a specific order:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "The table of staff members needs to be updated first."
        }), "\n", _jsx(_components.li, {
          children: "Then the staff permissions can be updated."
        }), "\n", _jsx(_components.li, {
          children: "Separately, a table of customer prospects needs to be updated."
        }), "\n", _jsx(_components.li, {
          children: "The final sync updates staff member assignments to customer prospects."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences-flow-chart.png",
          alt: "Creating a sequence in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "To represent this sequence of syncs, you can create a sequence that executes these four syncs in order with the following error handling:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The staff member sync terminates the sequence if it fatally errors or has too many rejected rows."
        }), "\n", _jsx(_components.li, {
          children: "The second and third syncs only terminate the sequence if they fatally error."
        }), "\n", _jsx(_components.li, {
          children: "The final staff member assignment sync doesn't check for errors, so the sequence completes successfully even if this sync fails."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences-high-level.png",
          alt: "Creating a sequence in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "sequence-setup",
        children: "Sequence setup"
      }), "\n", _jsxs(_components.p, {
        children: ["Before you create a sequence, ensure that all syncs you plan to use in it have their schedule type set to ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui#schedule-types",
          children: _jsx(_components.strong, {
            children: "Manual"
          })
        }), ". You can configure a sync to have a set schedule and use it in a sequence simultaneously, but we recommend against it unless you have a particular reason to do so."]
      }), "\n", _jsxs(_components.p, {
        children: ["Then, to create a new sequence, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sequences",
          children: [_jsx(_components.strong, {
            children: "Sequences"
          }), " page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add a sequence"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "add-syncs-to-the-sequence",
        children: "Add syncs to the sequence"
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "Add syncs"
        }), " to select syncs for the new sequence. You can include as many syncs as you want and add more later as needed."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences-add-syncs.png",
          alt: "Creating a sequence in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "configuring-sync-order-and-error-handling",
        children: "Configuring sync order and error handling"
      }), "\n", _jsx(_components.p, {
        children: "Once you've added syncs to the sequence, you can re-order them using the up and down arrows."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences-order.png",
          alt: "Creating a sequence in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can configure each sync's error-handling behavior using the ", _jsx(_components.strong, {
          children: "Terminate"
        }), " drop-down menu."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences-error-handling.png",
          alt: "Creating a sequence in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-your-sequence-schedule",
        children: "Set your sequence schedule"
      }), "\n", _jsxs(_components.p, {
        children: ["To finalize your sequence, give it a name and define its schedule type. You have all the same options for sequence scheduling as for ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui#schedule-types",
          children: "sync scheduling"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences-scheduling.png",
          alt: "Creating a sequence in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once you save your new sequence, it's visible on the main ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sequences",
          children: [_jsx(_components.strong, {
            children: "Sequences"
          }), " page"]
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sequence-management",
        children: "Sequence management"
      }), "\n", _jsxs(_components.p, {
        children: ["You can click a sequence from the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sequences",
          children: [_jsx(_components.strong, {
            children: "Sequences"
          }), " page"]
        }), " to go to its details page. There you can:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Disable or enable it"
        }), "\n", _jsx(_components.li, {
          children: "Run it manually"
        }), "\n", _jsx(_components.li, {
          children: "See the sequence's run history"
        }), "\n", _jsx(_components.li, {
          children: "Or update its configuration or schedule"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences-run-history.png",
          alt: "Managing a sequence in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Clicking on a particular run displays its details in a pop-up, including the execution time and final state of each sync run belonging to the sequence run."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences-run-summary.png",
          alt: "Managing a sequence in the Hightouch UI"
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
